import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

/**
 * 主体定义，用于在提示词中引用
 */
export interface ViduQ2ReferenceSubject {
  /**
   * 主体id，在提示词中可以通过 @主体id 的方式使用
   * 可以使用任意字符串，建议使用有意义的名称（如 'person', 'dog', 'car' 等）
   * 例如：id: 'girl' 可以在 prompt 中使用 '@girl 在跳舞'
   */
  id: string;
  /**
   * 该主体对应的图片url列表，每个主体最多支持3张图片
   */
  images: string[];
}

export interface ViduQ2I2vReferenceParams {
  /**
   * 文本提示词，必填
   * 使用 subjects 时，可以通过 @主体id 来引用主体内容
   * 例如："@1 和 @2 在公园里一起玩耍"
   * 注：字符长度不能超过 2000 个字符
   */
  prompt: string;
  /**
   * 主体列表（推荐使用），与 images 参数互斥
   * 每个主体包含：
   * - id: 主体ID，在 prompt 中通过 @id 引用
   * - images: 该主体的图片URL列表（每个主体最多3张图片）
   *
   * 推荐使用 subjects 而非 images，支持多主体精确控制
   *
   * @example
   * subjects: [
   *   { id: '1', images: ['https://example.com/person1.jpg'] },
   *   { id: '2', images: ['https://example.com/dog.jpg'] }
   * ]
   * prompt: '@1 和 @2 在公园里一起玩耍'
   */
  subjects?: ViduQ2ReferenceSubject[];
  /**
   * 参考图像列表（已废弃，推荐使用 subjects），与 subjects 参数互斥
   * 支持1-7张参考图片
   *
   * @deprecated 建议使用 subjects 参数以获得更好的主体控制
   */
  images?: string[];
  /**
   * 音色ID，使用预设音色或复刻音色
   * 为空时系统会自动推荐音色并使用
   */
  voice_id?: string;
  /**
   * 是否使用音视频直出能力，默认false
   * - true：使用音视频直出能力，同时bgm参数不生效
   * - false：不使用音视频直出能力，且bgm参数生效
   * @default false
   */
  audio?: boolean;
  /**
   * 视频时长（秒），范围1-8秒
   * @range 1 - 8
   */
  duration?: number;
  /**
   * 分辨率参数，默认值依据模型和视频时长而定
   * viduq2（1-10秒）：默认 720p, 可选：540p、720p、1080p
   * @default "720p"
   * @values 540p | 720p | 1080p
   */
  resolution?: '540p' | '720p' | '1080p';
  /**
   * 随机种子，默认0（随机）
   * @default 0
   */
  seed?: number;
  /**
   * 宽高比，默认16:9
   * @default "16:9"
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 运动幅度，默认auto
   * @default "auto"
   * @values auto | small | medium | large
   */
  movement_amplitude?: 'auto' | 'small' | 'medium' | 'large';
  /**
   * 是否添加背景音乐，默认false
   * 当audio=true时此参数不生效
   * @default false
   */
  bgm?: boolean;
  /**
   * 是否在非高峰时段处理，默认false
   * @default false
   */
  off_peak?: boolean;
  /**
   * 是否添加水印，默认false
   * @default false
   */
  watermark?: boolean;
  /**
   * 透传参数，最大1048576字符
   */
  payload?: string;
  /**
   * 回调URL，必须以http://或https://开头
   */
  callback_url?: string;
}

export interface ViduQ2I2vReferenceResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * viduQ2I2vReference
 *
 * Vidu Q2 参考图生视频（升级版 - 推荐使用 subjects）
 *
 * Model: viduq2_i2v_reference (最新升级模型)
 * Features:
 * - 支持多主体精确控制（通过 subjects 参数）
 * - 高质量视频输出，支持主体一致性
 * - 支持音视频直出能力（audio=true）
 * - 每个主体最多支持3张参考图片
 * Duration: 1-8秒
 * Resolution: 540p | 720p | 1080p
 *
 * 重要升级：
 * - ✅ 推荐使用 subjects 参数（支持多主体和 @id 引用）
 * - ⚠️  images 参数已废弃，建议迁移到 subjects
 *
 * API Documentation: https://platform.vidu.cn/docs/reference-to-video
 *
 * @example
 * ```typescript
 * import { initSeacloud, viduQ2I2vReference } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * // 推荐方式：使用 subjects（支持多主体精确控制）
 * // id 可以使用任意字符串，建议使用有意义的名称
 * const result = await viduQ2I2vReference({
 *   prompt: '@person 和 @dog 在公园里一起玩耍',
 *   subjects: [
 *     {
 *       id: 'person',  // 使用有意义的 id
 *       images: ['https://example.com/person.jpg']
 *     },
 *     {
 *       id: 'dog',  // 使用有意义的 id
 *       images: ['https://example.com/dog.jpg']
 *     }
 *   ],
 *   duration: 4,
 *   resolution: '720p',
 *   audio: true,  // 启用音视频直出
 * });
 *
 * // 多图参考（每个主体最多3张图片）
 * const result2 = await viduQ2I2vReference({
 *   prompt: '@girl 在跳舞',
 *   subjects: [
 *     {
 *       id: 'girl',  // id 可以是任意字符串
 *       images: [
 *         'https://example.com/person1.jpg',
 *         'https://example.com/person2.jpg',
 *         'https://example.com/person3.jpg'
 *       ]
 *     }
 *   ],
 *   duration: 8,
 * });
 *
 * console.log(result[0].url); // 返回生成的视频URL
 * ```
 *
 * @param params 请求参数，详见 ViduQ2I2vReferenceParams 接口定义
 * @returns 生成的资源列表
 */
export async function viduQ2I2vReference(
  params: ViduQ2I2vReferenceParams,
): Promise<ViduQ2I2vReferenceResource[]> {
  const client = await getClient();

  // 参数验证
  if (params.images && params.subjects) {
    throw new Error('images 和 subjects 参数互斥，只能选择其中一个');
  }

  if (!params.images && !params.subjects) {
    throw new Error('必须提供 images 或 subjects 参数之一');
  }

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 120, // 增加到 120 次 (360秒) 以支持多图参考
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'viduq2_i2v_reference',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: ViduQ2I2vReferenceResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Validate URL before adding to results
          validateResourceUrl(resource, 'video');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
